import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Radar, Lock, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This reset link is missing its token. Please request a new one.");
    }
  }, [token]);

  const passwordsMatch = password.length > 0 && password === confirm;
  const strongEnough = password.length >= 8;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token || !strongEnough || !passwordsMatch) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(
        `${API}/auth/reset-password`,
        { token, password },
        { withCredentials: true }
      );
      setSuccess(true);
      // Auto-redirect to login after 3s so the user sees the confirmation.
      setTimeout(() => navigate("/login", { replace: true }), 3000);
    } catch (err) {
      setError(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="reset-password-page"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0b1120] px-6"
    >
      <div className="w-full max-w-sm">
        <Link to="/" className="mb-6 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center border border-[#3b82f6]/40 bg-[#3b82f6]/10">
            <Radar className="h-5 w-5 text-[#3b82f6]" />
          </div>
          <div className="leading-tight">
            <div className="text-base font-bold tracking-tight text-[#f8fafc]">
              SIGNAL EDGE
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#64748b]">
              convergence intel
            </div>
          </div>
        </Link>

        <div className="border border-[#1e293b] bg-[#111827] p-6">
          {success ? (
            <div data-testid="reset-password-success">
              <div className="mb-3 flex items-center gap-2 text-[#22c55e]">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em]">
                  Password updated
                </span>
              </div>
              <h1 className="text-xl font-bold tracking-tight text-[#f8fafc]">
                All set
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-[#94a3b8]">
                Your password has been updated. You'll be redirected to sign
                in — or click the link below.
              </p>
              <Link
                to="/login"
                data-testid="reset-password-login-link"
                className="mt-6 inline-block bg-[#3b82f6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2563eb]"
              >
                Sign in now
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">
                Set a new password
              </div>
              <h1 className="text-xl font-bold tracking-tight text-[#f8fafc]">
                Choose your new password
              </h1>
              <p className="mt-1 text-sm text-[#94a3b8]">
                Minimum 8 characters. Use something you don't reuse elsewhere.
              </p>

              <form onSubmit={onSubmit} className="mt-5 space-y-3">
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748b]" />
                  <input
                    type="password"
                    autoFocus
                    required
                    minLength={8}
                    autoComplete="new-password"
                    data-testid="reset-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="new password"
                    disabled={loading || !token}
                    className="w-full border border-[#1e293b] bg-[#0f172a] py-2.5 pl-10 pr-3 text-sm text-[#f8fafc] placeholder:text-[#64748b] focus:border-[#3b82f6] focus:outline-none focus:ring-1 focus:ring-[#3b82f6] disabled:opacity-60"
                  />
                </div>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748b]" />
                  <input
                    type="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    data-testid="reset-password-confirm-input"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="confirm new password"
                    disabled={loading || !token}
                    className="w-full border border-[#1e293b] bg-[#0f172a] py-2.5 pl-10 pr-3 text-sm text-[#f8fafc] placeholder:text-[#64748b] focus:border-[#3b82f6] focus:outline-none focus:ring-1 focus:ring-[#3b82f6] disabled:opacity-60"
                  />
                </div>

                {password.length > 0 && !strongEnough && (
                  <div className="font-mono text-[11px] text-[#f59e0b]">
                    At least 8 characters.
                  </div>
                )}
                {confirm.length > 0 && password !== confirm && (
                  <div className="font-mono text-[11px] text-[#f59e0b]">
                    Passwords don't match.
                  </div>
                )}

                {error && (
                  <div
                    data-testid="reset-password-error"
                    className="flex items-start gap-2 border border-[#dc2626]/40 bg-[#dc2626]/10 p-2.5 font-mono text-[11px] text-[#f87171]"
                  >
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !token || !strongEnough || !passwordsMatch}
                  data-testid="reset-password-submit-button"
                  className="flex w-full items-center justify-center gap-2 bg-[#3b82f6] py-2.5 text-sm font-semibold text-white transition hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Updating…
                    </>
                  ) : (
                    "Update password"
                  )}
                </button>
              </form>

              <div className="mt-5 border-t border-[#1e293b] pt-4 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-[#64748b]">
                Need a fresh link?{" "}
                <Link
                  to="/forgot-password"
                  data-testid="reset-password-request-new-link"
                  className="text-[#60a5fa] hover:text-[#3b82f6]"
                >
                  Request one
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
